import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Users, User, Building2, Mail, History, Sparkles, CheckCircle2, XCircle, Search, Eye, AlarmClock, PackageCheck, FileText, X } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Skeleton, Modal, Badge } from "@/components/ui-kit";

/**
 * Iter v34b0 — Pengumuman (Broadcast Email) — pengganti Chat lama.
 * Iter v34bq — Riwayat: klik → Modal detail (subject, body, list penerima).
 *              Search box untuk memfilter penerima spesifik.
 *              + 2 tombol pengingat PO (belum-supply / belum-tagih) trigger email.
 */
export default function Pengumuman() {
  const [target, setTarget] = useState("klien");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [specificIds, setSpecificIds] = useState([]);
  const [specificSearch, setSpecificSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [detailAnno, setDetailAnno] = useState(null);
  // Iter v34bq — Trigger reminder PO
  const [poReminderBusy, setPoReminderBusy] = useState(null); // "supply" | "billing" | null
  // Iter v34br (Task 4) — Template pengumuman dari Settings.
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    api.get("/settings").then((r) => setTemplates(Array.isArray(r.data.announcement_templates) ? r.data.announcement_templates : [])).catch(() => {});
    loadHistory();
  }, []);
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const r = await api.get("/pengumuman/history?limit=30");
      setHistory(r.data || []);
    } finally { setLoadingHistory(false); }
  };

  const eligibleUsers = useMemo(() => users.filter((u) =>
    (u.account_type === "klien" || u.account_type === "karyawan") && u.email && u.is_active !== false
  ), [users]);

  const filteredUsers = useMemo(() => {
    const q = specificSearch.trim().toLowerCase();
    if (!q) return eligibleUsers;
    return eligibleUsers.filter((u) =>
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.account_type || "").toLowerCase().includes(q)
    );
  }, [eligibleUsers, specificSearch]);

  const recipientCount = useMemo(() => {
    if (target === "specific") return specificIds.length;
    if (target === "klien") return eligibleUsers.filter((u) => u.account_type === "klien").length;
    if (target === "karyawan") return eligibleUsers.filter((u) => u.account_type === "karyawan").length;
    if (target === "all") return eligibleUsers.length;
    return 0;
  }, [target, specificIds, eligibleUsers]);

  const send = async () => {
    if (!subject.trim()) { toast.error("Subject wajib diisi"); return; }
    if (!body.trim()) { toast.error("Isi pengumuman wajib diisi"); return; }
    if (target === "specific" && specificIds.length === 0) { toast.error("Pilih minimal 1 penerima"); return; }
    if (!window.confirm(`Kirim pengumuman ke ${recipientCount} penerima?`)) return;
    setSending(true);
    try {
      const r = await api.post("/pengumuman/send", {
        target, specific_ids: target === "specific" ? specificIds : [],
        subject: subject.trim(),
        body_html: body,
      });
      const { sent_count, failed_count, recipient_count } = r.data || {};
      toast.success(`Terkirim: ${sent_count}/${recipient_count} penerima${failed_count ? ` (${failed_count} gagal)` : ""}`);
      setSubject("");
      setBody("");
      setSpecificIds([]);
      setSpecificSearch("");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim pengumuman");
    } finally { setSending(false); }
  };

  const toggleSpecific = (id) => {
    setSpecificIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllVisible = () => {
    // Iter v34bq — Pilih semua yang ter-filter (bukan seluruh eligibleUsers).
    setSpecificIds((prev) => {
      const merged = new Set(prev);
      filteredUsers.forEach((u) => merged.add(u.id));
      return Array.from(merged);
    });
  };
  const clearSpecific = () => setSpecificIds([]);

  // Iter v34bq — Trigger reminder PO belum supply / belum tagih.
  const triggerPoReminder = async (kind) => {
    // kind: "supply" | "billing"
    const label = kind === "supply" ? "PO belum di-supply" : "PO belum ditagih";
    if (!window.confirm(`Kirim pengingat "${label}" ke daftar penerima yang tersimpan di Pengaturan Database?`)) return;
    setPoReminderBusy(kind);
    try {
      const r = await api.post(`/pengumuman/reminders/po-${kind}`);
      const { po_count, sent_count, failed_count, recipient_count } = r.data || {};
      if (recipient_count === 0) {
        toast.error(`Belum ada penerima email pengingat ${label} di Pengaturan Database.`);
      } else if (po_count === 0) {
        toast.success(`Tidak ada ${label} — semua PO aman.`);
      } else {
        toast.success(`Pengingat ${label} terkirim: ${sent_count}/${recipient_count} penerima (${po_count} PO)${failed_count ? ` · ${failed_count} gagal` : ""}`);
      }
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Gagal mengirim pengingat ${label}`);
    } finally { setPoReminderBusy(null); }
  };

  return (
    <div className="min-h-full bg-paper p-4 md:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <PageHeader
          overline="Sistem · Komunikasi"
          title="Pengumuman"
          description="Kirim pengumuman via email ke klien atau karyawan. Email dikirim dari alamat no-reply — penerima tidak dapat membalas."
        />

        {/* Iter v34bq — Pengingat PO cepat */}
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlarmClock className="h-4 w-4 text-terra-600"/>
            <h3 className="text-sm font-semibold text-stone-800">Pengingat PO Otomatis</h3>
            <span className="text-[10px] text-stone-500">Daftar penerima diatur di <b>Pengaturan Database</b>.</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => triggerPoReminder("supply")}
              disabled={poReminderBusy === "supply"}
              className="flex items-start gap-3 border border-stone-200 hover:border-amber-400 hover:bg-amber-50 rounded-xl p-4 text-left transition disabled:opacity-60"
              data-testid="po-reminder-supply-btn"
            >
              <PackageCheck className="h-5 w-5 text-amber-700 shrink-0 mt-0.5"/>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-stone-900">PO Belum Di-supply</div>
                <div className="text-xs text-stone-500 mt-0.5">Kirim reminder untuk PO yang qty tersuplai &lt; qty PO.</div>
                <div className="text-[10px] text-amber-700 font-semibold mt-1 uppercase tracking-wider">
                  {poReminderBusy === "supply" ? "Mengirim..." : "Klik untuk kirim →"}
                </div>
              </div>
            </button>
            <button
              onClick={() => triggerPoReminder("billing")}
              disabled={poReminderBusy === "billing"}
              className="flex items-start gap-3 border border-stone-200 hover:border-red-400 hover:bg-red-50 rounded-xl p-4 text-left transition disabled:opacity-60"
              data-testid="po-reminder-billing-btn"
            >
              <FileText className="h-5 w-5 text-red-700 shrink-0 mt-0.5"/>
              <div className="min-w-0">
                <div className="font-semibold text-sm text-stone-900">PO Belum Ditagih</div>
                <div className="text-xs text-stone-500 mt-0.5">Kirim reminder untuk PO yang sudah supply tapi belum di-invoice.</div>
                <div className="text-[10px] text-red-700 font-semibold mt-1 uppercase tracking-wider">
                  {poReminderBusy === "billing" ? "Mengirim..." : "Klik untuk kirim →"}
                </div>
              </div>
            </button>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <Card className="lg:col-span-2 p-6 space-y-5">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-2">Target Penerima</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <TargetChip label="Semua Klien" icon={Building2} active={target === "klien"} onClick={() => { setTarget("klien"); setSpecificIds([]); }} testid="target-klien"/>
                <TargetChip label="Semua Karyawan" icon={Users} active={target === "karyawan"} onClick={() => { setTarget("karyawan"); setSpecificIds([]); }} testid="target-karyawan"/>
                <TargetChip label="Semua (Klien + Karyawan)" icon={Sparkles} active={target === "all"} onClick={() => { setTarget("all"); setSpecificIds([]); }} testid="target-all"/>
                <TargetChip label="Pilih Spesifik" icon={User} active={target === "specific"} onClick={() => setTarget("specific")} testid="target-specific"/>
              </div>
              <div className="mt-2 text-xs text-stone-500 flex items-center gap-2">
                <Mail className="h-3 w-3"/>
                <span>Estimasi penerima: <b>{recipientCount}</b> email</span>
              </div>
            </div>

            {target === "specific" && (
              <div className="border border-stone-200 rounded-2xl p-3 max-h-80 overflow-y-auto bg-stone-50/60" data-testid="specific-list">
                <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold">Pilih Penerima ({specificIds.length} dipilih)</span>
                  <div className="flex gap-2">
                    <button onClick={selectAllVisible} className="text-[10px] uppercase tracking-widest text-blue-700 hover:underline">Pilih Semua {specificSearch ? "yang cocok" : ""}</button>
                    <button onClick={clearSpecific} className="text-[10px] uppercase tracking-widest text-stone-500 hover:underline">Bersihkan</button>
                  </div>
                </div>
                {/* Iter v34bq — Kolom pencarian */}
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none"/>
                  <Input
                    value={specificSearch}
                    onChange={(e) => setSpecificSearch(e.target.value)}
                    placeholder="Cari nama, email, atau tipe akun..."
                    className="!pl-8 !py-1.5 text-sm"
                    data-testid="specific-search"
                  />
                  {specificSearch && (
                    <button
                      onClick={() => setSpecificSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-red-700"
                      title="Bersihkan pencarian"
                    >
                      <X className="h-3.5 w-3.5"/>
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-stone-500 mb-1.5">
                  {filteredUsers.length} dari {eligibleUsers.length} penerima tersedia
                  {specificSearch && ` · Pencarian: "${specificSearch}"`}
                </div>
                {filteredUsers.map((u) => (
                  <label key={u.id} className="flex items-center gap-2 py-1.5 px-2 hover:bg-white rounded cursor-pointer">
                    <input type="checkbox" checked={specificIds.includes(u.id)} onChange={() => toggleSpecific(u.id)} data-testid={`specific-${u.id}`}/>
                    <span className="text-sm text-stone-800 flex-1">{u.name}</span>
                    <span className="text-[10px] uppercase tracking-widest text-stone-400">{u.account_type}</span>
                    <span className="text-xs text-stone-500 font-mono-num">{u.email}</span>
                  </label>
                ))}
                {filteredUsers.length === 0 && <div className="text-center text-xs text-stone-400 py-6">
                  {specificSearch ? `Tidak ada penerima cocok dengan "${specificSearch}"` : "Tidak ada penerima tersedia"}
                </div>}
              </div>
            )}

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Subject Email</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="mis: Info Libur Nasional / Perubahan Harga Semen" data-testid="anno-subject"/>
              {/* Iter v34br (Task 4) — Dropdown template pengumuman */}
              {templates.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className="text-stone-500 uppercase tracking-widest text-[10px]">Template:</span>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => {
                      const idx = parseInt(e.target.value, 10);
                      setSelectedTemplate(e.target.value);
                      if (!isNaN(idx) && templates[idx]) {
                        setSubject(templates[idx].subject || "");
                        setBody(templates[idx].body_html || "");
                        toast.success(`Template "${templates[idx].name}" dimuat`);
                      }
                    }}
                    className="border border-stone-300 rounded-md px-2 py-1 text-xs bg-white"
                    data-testid="anno-template-select"
                  >
                    <option value="">— Pilih Template —</option>
                    {templates.map((t, idx) => <option key={idx} value={idx}>{t.name || `Template ${idx + 1}`}</option>)}
                  </select>
                  {selectedTemplate !== "" && (
                    <button type="button" onClick={() => { setSelectedTemplate(""); setSubject(""); setBody(""); }} className="text-stone-400 hover:text-red-700 text-xs">
                      × Reset
                    </button>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-stone-600 mb-1.5">Isi Pengumuman</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                placeholder="Tulis isi pengumuman di sini... Anda bisa menggunakan HTML dasar seperti <p>, <b>, <br>, <ul>, <li>."
                className="w-full border border-stone-300 rounded-xl px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-y font-mono-num"
                data-testid="anno-body"
              />
              <p className="text-[10px] text-stone-400 mt-1">HTML sederhana didukung. Email dikirim dari alamat no-reply — penerima tidak dapat membalas email ini.</p>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-stone-100">
              <Button onClick={send} disabled={sending || recipientCount === 0} data-testid="anno-send-btn">
                <Send className="h-3.5 w-3.5"/> {sending ? "Mengirim..." : `Kirim ke ${recipientCount} Penerima`}
              </Button>
            </div>
          </Card>

          {/* History sidebar — Iter v34bq: klik row untuk detail */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-stone-500"/>
              <h3 className="text-sm font-semibold text-stone-800">Riwayat Pengumuman</h3>
            </div>
            {loadingHistory ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full"/>)}
              </div>
            ) : history.length === 0 ? (
              <div className="text-center text-xs text-stone-400 py-8">Belum ada riwayat</div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto" data-testid="anno-history">
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setDetailAnno(h)}
                    className="w-full text-left border border-stone-100 rounded-xl p-3 hover:bg-stone-50/50 hover:border-blue-300 transition"
                    data-testid={`anno-history-${h.id}`}
                    title="Klik untuk melihat detail"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm text-stone-900 truncate flex items-center gap-1.5">
                          <Eye className="h-3 w-3 text-stone-400 shrink-0"/>{h.subject}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
                          {new Date(h.created_at).toLocaleString("id-ID", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </div>
                      <TargetPill target={h.target}/>
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-xs">
                      <span className="flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3"/> {h.sent_count}
                      </span>
                      {h.failed_count > 0 && (
                        <span className="flex items-center gap-1 text-red-600">
                          <XCircle className="h-3 w-3"/> {h.failed_count}
                        </span>
                      )}
                      <span className="text-stone-500">dari {h.recipient_count} penerima</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Iter v34bq — Detail modal riwayat pengumuman */}
      <Modal open={!!detailAnno} onClose={() => setDetailAnno(null)} title={detailAnno?.subject || "Detail Pengumuman"} size="lg">
        {detailAnno && (
          <div className="space-y-4 text-sm" data-testid="anno-detail-modal">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <TargetPill target={detailAnno.target}/>
              <Badge tone="stone">{new Date(detailAnno.created_at).toLocaleString("id-ID")}</Badge>
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5"/> {detailAnno.sent_count} terkirim</span>
              {detailAnno.failed_count > 0 && (
                <span className="inline-flex items-center gap-1 text-red-600"><XCircle className="h-3.5 w-3.5"/> {detailAnno.failed_count} gagal</span>
              )}
              <span className="text-stone-500">dari {detailAnno.recipient_count} penerima</span>
              {detailAnno.sent_by_name && <span className="text-stone-500">· oleh {detailAnno.sent_by_name}</span>}
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">Isi Pengumuman</div>
              <div
                className="border border-stone-200 rounded-lg p-4 bg-white text-sm leading-relaxed"
                dangerouslySetInnerHTML={{ __html: detailAnno.body_html || "<i>(kosong)</i>" }}
              />
            </div>

            {detailAnno.target === "specific" && (detailAnno.specific_ids?.length || 0) > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
                  Target Penerima Spesifik ({detailAnno.specific_ids.length} orang)
                </div>
                <div className="border border-stone-200 rounded-lg max-h-52 overflow-y-auto bg-stone-50/50">
                  {detailAnno.specific_ids.map((uid) => {
                    const u = users.find((x) => x.id === uid);
                    return (
                      <div key={uid} className="flex items-center gap-2 py-1.5 px-3 border-b border-stone-100 last:border-0 text-xs">
                        <User className="h-3 w-3 text-stone-400"/>
                        <span className="flex-1 text-stone-800">{u?.name || <i className="text-stone-400">(user #{uid.slice(0, 8)} · tidak aktif)</i>}</span>
                        {u?.account_type && <span className="text-[10px] uppercase tracking-widest text-stone-400">{u.account_type}</span>}
                        {u?.email && <span className="text-stone-500 font-mono-num">{u.email}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {detailAnno.failed_emails && detailAnno.failed_emails.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-red-600 mb-1">Email Gagal Dikirim</div>
                <div className="border border-red-200 rounded-lg p-3 bg-red-50 text-xs font-mono-num text-red-800 space-y-0.5">
                  {detailAnno.failed_emails.map((e, i) => <div key={i}>· {e}</div>)}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2 border-t border-stone-100">
              <Button variant="ghost" onClick={() => setDetailAnno(null)}>Tutup</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function TargetChip({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-start gap-1 p-3 rounded-xl border transition text-left ${
        active ? "bg-blue-800 text-white border-blue-800" : "bg-white border-stone-200 text-stone-700 hover:border-blue-500 hover:text-blue-800"
      }`}
      data-testid={testid}
    >
      <Icon className={`h-4 w-4 ${active ? "text-emerald-300" : "text-stone-500"}`}/>
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}

function TargetPill({ target }) {
  const map = {
    klien: { label: "Klien", color: "bg-blue-100 text-blue-800" },
    karyawan: { label: "Karyawan", color: "bg-emerald-100 text-emerald-800" },
    all: { label: "Semua", color: "bg-purple-100 text-purple-800" },
    specific: { label: "Spesifik", color: "bg-amber-100 text-amber-800" },
    po_supply_reminder: { label: "Reminder PO Supply", color: "bg-orange-100 text-orange-800" },
    po_billing_reminder: { label: "Reminder PO Tagih", color: "bg-red-100 text-red-800" },
  };
  const { label, color } = map[target] || { label: target, color: "bg-stone-100 text-stone-700" };
  return <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${color}`}>{label}</span>;
}
